// =============================================================================
// features/admin/feature-flags/AudienceEditorDrawer.tsx — Drawer de edição da
// audience de uma flag (F32-S04).
//
// Edita os 4 eixos (papéis, cidades, organizações, usuários) e envia o objeto
// `audience` completo no PATCH — o backend SUBSTITUI por inteiro, não mescla
// por eixo (apps/api/src/modules/featureFlags/repository.ts#updateFlag).
//
// Status (enabled/disabled/internal_only) é só leitura aqui — a edição
// continua no toggle da tabela (FlagsTable), evitando dois caminhos de
// mutação para o mesmo campo.
// =============================================================================

import * as React from 'react';
import { createPortal } from 'react-dom';

import { Button } from '../../../components/ui/Button';
import { useUpdateFeatureFlag } from '../../../hooks/admin/useFeatureFlags';
import type { FeatureFlagDto } from '../../../hooks/admin/useFeatureFlags.types';
import { useCitiesList } from '../../../hooks/useCitiesList';
import { useAuth } from '../../../lib/auth-store';
import { cn } from '../../../lib/cn';

import { AudienceEditorForm } from './AudienceEditorForm';
import type { AudienceDraft } from './audienceImpact';
import { audienceDraftToPayload, audienceToDraft } from './audienceImpact';

interface AudienceEditorDrawerProps {
  open: boolean;
  onClose: () => void;
  flag: FeatureFlagDto | null;
}

function draftKey(draft: AudienceDraft): string {
  return JSON.stringify({
    roles: [...draft.roles].sort(),
    city_ids: [...draft.city_ids].sort(),
    organization_ids: [...draft.organization_ids].sort(),
    user_ids: [...draft.user_ids].sort(),
  });
}

export function AudienceEditorDrawer({
  open,
  onClose,
  flag,
}: AudienceEditorDrawerProps): React.JSX.Element | null {
  const { hasPermission } = useAuth();
  const canManage = hasPermission('flags:manage');
  const { cities } = useCitiesList();
  const { update, isPending } = useUpdateFeatureFlag({ onSuccess: onClose });

  const originalDraft = React.useMemo(() => audienceToDraft(flag?.audience), [flag]);
  const [draft, setDraft] = React.useState<AudienceDraft>(originalDraft);

  // Sincroniza o form local sempre que uma flag DIFERENTE é aberta no drawer
  // (não é fetch — é reidratação de prop pra estado de edição, padrão comum
  // em drawers de formulário do projeto, ex: UserDrawer).
  React.useEffect(() => {
    setDraft(originalDraft);
  }, [originalDraft]);

  React.useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  if (!open || !flag) return null;

  const isDirty = draftKey(draft) !== draftKey(originalDraft);

  function handleSave(): void {
    if (!canManage || !flag) return;
    update(flag.key, { audience: audienceDraftToPayload(draft) });
  }

  return createPortal(
    <>
      <div
        role="presentation"
        aria-hidden="true"
        className="fixed inset-0 z-[150] bg-[var(--text)]/20 backdrop-blur-[2px]"
        onClick={onClose}
        style={{ animation: 'fade-in 200ms ease both' }}
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="audience-drawer-title"
        className={cn(
          'fixed right-0 top-0 bottom-0 z-[160]',
          'w-full sm:max-w-[540px]',
          'flex flex-col border-l border-border overflow-y-auto',
        )}
        style={{
          background: 'var(--bg-elev-1)',
          boxShadow: 'var(--elev-5)',
          animation: 'slide-in-right 300ms cubic-bezier(0.16,1,0.3,1) both',
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-border-subtle shrink-0">
          <div>
            <h2
              id="audience-drawer-title"
              className="font-display font-bold text-ink"
              style={{
                fontSize: 'var(--text-xl)',
                letterSpacing: '-0.03em',
                fontVariationSettings: "'opsz' 24",
              }}
            >
              Audiência
            </h2>
            <code className="font-mono text-xs text-azul">{flag.key}</code>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            className="w-8 h-8 flex items-center justify-center rounded-sm text-ink-3 hover:text-ink hover:bg-surface-hover transition-all duration-fast ease focus-visible:ring-2 focus-visible:ring-azul/20 focus-visible:outline-none"
          >
            <svg
              viewBox="0 0 20 20"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.6}
              className="w-5 h-5"
              aria-hidden="true"
            >
              <path d="M5 5l10 10M15 5l-10 10" />
            </svg>
          </button>
        </div>

        {!canManage ? (
          <div className="flex items-center justify-center flex-1 px-6 py-10">
            <p className="font-sans text-sm text-ink-3 text-center max-w-xs">
              Você não tem permissão (<code className="font-mono text-xs">flags:manage</code>) para
              editar a audiência desta flag.
            </p>
          </div>
        ) : (
          <AudienceEditorForm
            status={flag.status}
            draft={draft}
            onChange={setDraft}
            cities={cities}
          />
        )}

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-border-subtle shrink-0">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={handleSave}
            disabled={!canManage || !isDirty || isPending}
          >
            {isPending ? 'Salvando…' : 'Salvar audiência'}
          </Button>
        </div>
      </div>
    </>,
    document.body,
  );
}
